import React from "react";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import { getIconComponent } from "@/Utils/getIconComponent";

export const OutLinedButton = ({
  variant,
  label,
  disabled,
  loading,
  type = "submit",
  onClick,
  iconName = null,
  iconPosition = "start",
  startAdornment = null,
  endAdornment = null,
  iconSpacing = 1, // Default spacing (8px)
  sx = {},
  width,
  height,
  ...props
}) => {
  const IconComponent = iconName
    ? getIconComponent(iconName, {
        textAlign: "center",
        color: "var(--brand-600)",
        size: 20,
        style: { fontWeight: "bold" },
      })
    : null;

  const defaultStyles = {
    textWrap: "nowrap",
    backgroundColor: "var(--white-600)",
    color: "var(--brand-900)",
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "none",
    height: height || "40px",
    width: width || "auto",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "var(--brand-600)", // now overrideable by sx.borderColor
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    gap: iconSpacing,
    cursor: disabled ? "not-allowed" : "pointer",
    "&:hover": {
      backgroundColor: "var(--white-700)",
    },
    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
      margin: "4px 0", // Remove default MUI icon margins
    },
  };

  const mergedStyles = {
    ...defaultStyles,
    ...sx,
    "&:hover": {
      ...defaultStyles["&:hover"],
      ...(sx["&:hover"] || {}),
    },
  };

  // Handle icon/adornment positioning
  const getContent = () => {
    if (loading) {
      return (
        <>
          <CircularProgress size={15} color="inherit" sx={{ mr: 1 }} />
          Loading...
        </>
      );
    }

    return (
      <>
        {(startAdornment || (iconPosition === "start" && IconComponent)) && (
          <Box
            sx={{
              display: "flex",
              // mr: iconSpacing // Add spacing after start adornment/icon
            }}
          >
            {startAdornment || IconComponent}
          </Box>
        )}

        <Typography variant={variant}>{label}</Typography>

        {(endAdornment || (iconPosition === "end" && IconComponent)) && (
          <Box
            sx={{
              display: "flex",
              // ml: iconSpacing // Add spacing before end adornment/icon
            }}
          >
            {endAdornment || IconComponent}
          </Box>
        )}
      </>
    );
  };

  return (
    <Button
      type={type}
      variant="outlined"
      disabled={disabled || loading}
      onClick={onClick}
      sx={mergedStyles}
      {...props}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: iconSpacing }}>
        {getContent()}
      </Box>
    </Button>
  );
};
